import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  AlertTriangle,
  ArrowUpRight,
  Building2,
  CheckCircle2,
  ClipboardList,
  Clock,
  CreditCard,
  ExternalLink,
  FileText,
  Landmark,
  Loader2,
  Pencil,
  Plus,
  RefreshCcw,
  Trash2,
  Unplug,
  Wallet,
} from 'lucide-react'
import { Page } from '@/components/Page'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { FormField } from '@/components/FormField'
import { Modal } from '@/components/Modal'
import { tokenManager } from '@/lib/tokenManager'
import { useDeleteWorkspace, useRequestDeleteWorkspaceCode } from '@/auth/useMemberships'
import { useQueryClient } from '@tanstack/react-query'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { useToast } from '@/hooks/useToast'
import { useDirty } from '@/hooks/useDirty'
import { useAuth } from '@/auth/useAuth'
import { ApiError } from '@/lib/api'
import { cn } from '@/lib/cn'
import { COUNTRY_LOCALE_MAP, COUNTRY_NAMES, defaultDateFormatForCountry, resetLocaleCache } from '@/lib/locale'
import { CountryCombobox } from '@/components/CountryCombobox'
import { Combobox } from '@/components/Combobox'
import { FlagIcon } from '@/components/FlagIcon'
import {
  useCreateTenantBankAccount,
  useDeleteTenantBankAccount,
  usePostingConfig,
  useTenantBankAccounts,
  useTenantSettings,
  useUpdatePostingConfig,
  useUpdateTenantBankAccount,
  useUpdateTenantSettings,
} from '@/features/company/queries'
import { POSTING_SOURCE_LABELS } from '@/features/company/api'
import type { TenantBankAccount, TenantBankAccountPayload } from '@/features/company/types'
import {
  useDisconnectStripe,
  useInvoicingSettings,
  useOpenStripeDashboard,
  useRefreshStripeOnboardingLink,
  useSetStripeConnectMode,
  useStartStripeOnboarding,
  useStripeConnectCountries,
  useStripeConnectStatus,
  useSyncStripeConnect,
  useUpdateInvoicingSettings,
} from '@/features/invoicing/queries'
import {
  usePayoutAccount,
  useSetPayoutAccount,
} from '@/features/invoicing/payments/queries'
import type { TenantSettingsPatch } from '@/features/company/types'
import {
  useBillingSubscription,
  useBillingUsage,
} from '@/features/subscriptions/queries'
import { BusinessInfoSection } from './BusinessInfoSection'

const COMPANY_TYPES = ['Sole Proprietor', 'LLC', 'Corporation', 'Other'] as const
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

const SESSION_OPTIONS: { value: string; label: string }[] = [
  { value: '8h', label: '8 Hours (business day)' },
  { value: '24h', label: '24 Hours' },
  { value: '7d', label: '7 Days (default)' },
  { value: '30d', label: '30 Days' },
  { value: 'tab_close', label: 'On Tab Close' },
]
const SESSION_LABEL = new Map(SESSION_OPTIONS.map((o) => [o.value, o.label.replace(' (default)', '')]))

export function CompanyPage() {
  const { hasPermission } = useAuth()
  const { toast } = useToast()
  const canEdit = hasPermission('tenant.settings.edit')
  const canEditSession = hasPermission('tenant.session.edit')

  const q = useTenantSettings()
  const m = useUpdateTenantSettings()

  type TabId =
    | 'general' | 'invoicing' | 'payments' | 'business-info' | 'posting' | 'subscription' | 'danger'
  // General lands first so the most-edited fields (business name, locale,
  // session) are one click away. Subscription & Usage now sits after
  // Business Info so the order reads identity → operations → billing.
  const [tab, setTab] = useState<TabId>('general')

  const [businessName, setBusinessName] = useState('')
  const [companyType, setCompanyType] = useState('')
  const [businessPhone, setBusinessPhone] = useState('')
  const [businessEmail, setBusinessEmail] = useState('')
  const [addressLine1, setAddressLine1] = useState('')
  const [addressLine2, setAddressLine2] = useState('')
  const [city, setCity] = useState('')
  const [country, setCountry] = useState('US')
  const [timezone, setTimezone] = useState('')
  const [fiscalMonth, setFiscalMonth] = useState('1')
  const [sessionTimeout, setSessionTimeout] = useState('7d')

  useEffect(() => {
    const t = q.data?.tenant
    if (!t) return
    setBusinessName(t.business_name ?? '')
    setCompanyType(t.company_type ?? '')
    setBusinessPhone(t.business_phone ?? '')
    setBusinessEmail(t.business_email ?? '')
    setAddressLine1(t.address_line1 ?? '')
    setAddressLine2(t.address_line2 ?? '')
    setCity(t.city ?? '')
    setCountry(t.country ?? 'US')
    setTimezone(t.timezone ?? '')
    setFiscalMonth(String(t.fiscal_year_start ?? 1))
    setSessionTimeout(t.session_timeout ?? '7d')
  }, [q.data])

  const timezones = useMemo<string[]>(() => {
    try {
      const fn = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] })
        .supportedValuesOf
      if (typeof fn === 'function') return fn('timeZone')
    } catch {
      /* ignore */
    }
    return Array.from(
      new Set(Object.values(COUNTRY_LOCALE_MAP).map((c) => c.tzDefault)),
    ).sort()
  }, [])

  // Snapshot the form for dirty-checking. Both `form` and `baseline` use
  // the SAME shape so useDirty's deepEqual returns true only when one of
  // these fields actually diverges from what's saved on the tenant.
  // Keeping this near the state declarations means new fields stay opt-in
  // (a new field that isn't added here won't unlock Save).
  const form = useMemo(
    () => ({
      businessName,
      companyType,
      businessPhone,
      businessEmail,
      addressLine1,
      addressLine2,
      city,
      country,
      timezone,
      fiscalMonth,
      sessionTimeout,
    }),
    [
      businessName, companyType, businessPhone, businessEmail,
      addressLine1, addressLine2, city, country, timezone,
      fiscalMonth, sessionTimeout,
    ],
  )
  const baseline = useMemo(() => {
    const t = q.data?.tenant
    if (!t) return null
    return {
      businessName: t.business_name ?? '',
      companyType: t.company_type ?? '',
      businessPhone: t.business_phone ?? '',
      businessEmail: t.business_email ?? '',
      addressLine1: t.address_line1 ?? '',
      addressLine2: t.address_line2 ?? '',
      city: t.city ?? '',
      country: t.country ?? 'US',
      timezone: t.timezone ?? '',
      fiscalMonth: String(t.fiscal_year_start ?? 1),
      sessionTimeout: t.session_timeout ?? '7d',
    }
  }, [q.data])
  const generalDirty = useDirty(form, baseline)

  const onCountryChange = (next: string) => {
    setCountry(next)
    const cfg = COUNTRY_LOCALE_MAP[next as keyof typeof COUNTRY_LOCALE_MAP]
    if (cfg) {
      setTimezone(cfg.tzDefault)
      setFiscalMonth(String(cfg.fiscalStartMonth))
    }
  }

  const preview = useMemo(() => {
    const cfg = COUNTRY_LOCALE_MAP[country as keyof typeof COUNTRY_LOCALE_MAP]
    if (!cfg) return null
    const loc = cfg.locale
    const now = new Date()
    const date = now.toLocaleDateString(loc, { year: 'numeric', month: 'short', day: 'numeric' })
    const currency = new Intl.NumberFormat(loc, {
      style: 'currency',
      currency: cfg.currency,
      minimumFractionDigits: 2,
    }).format(1234567.89)
    const number = new Intl.NumberFormat(loc).format(1234567.89)
    const fiscalIdx = Math.max(0, (parseInt(fiscalMonth, 10) || 1) - 1)
    const fiscal = new Date(2024, fiscalIdx).toLocaleString(loc, { month: 'long' })
    return { date, currency, number, fiscal }
  }, [country, fiscalMonth])

  const save = async () => {
    const body: TenantSettingsPatch = {}
    if (canEdit) {
      body.business_name = businessName
      body.company_type = companyType
      body.business_phone = businessPhone
      body.business_email = businessEmail
      body.address_line1 = addressLine1
      body.address_line2 = addressLine2
      body.city = city
      body.country = country || 'US'
      body.timezone = timezone || null
      body.fiscal_year_start = parseInt(fiscalMonth, 10) || 1
      // Derive currency / locale / date_format from the picked country so
      // the rest of the app (invoice currency default, formatters, PDFs,
      // notifications) sees the change without a separate currency picker.
      // Tenants who genuinely want a non-default mix (e.g. UK address but
      // USD invoicing) can override via a future "Advanced" toggle.
      const cfg = COUNTRY_LOCALE_MAP[(body.country) as keyof typeof COUNTRY_LOCALE_MAP]
      if (cfg) {
        body.currency = cfg.currency
        body.locale = cfg.locale
        body.date_format = defaultDateFormatForCountry(body.country)
      }
      // Mirror the legacy localStorage keys so lib/locale.ts picks up
      // the new values without a reload.
      try {
        localStorage.setItem('userCountry', body.country ?? '')
        if (body.timezone) localStorage.setItem('userTimezone', body.timezone)
        if (body.fiscal_year_start)
          localStorage.setItem('userFiscalStartMonth', String(body.fiscal_year_start))
      } catch {
        /* ignore */
      }
    }
    if (canEditSession) body.session_timeout = sessionTimeout
    if (Object.keys(body).length === 0) {
      toast({ title: 'No changes to save.' })
      return
    }
    try {
      await m.mutateAsync(body)
      resetLocaleCache()
      toast({ title: 'Company settings saved.' })
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Failed to save settings',
        description: messageFor(err, ''),
      })
    }
  }

  if (q.isLoading) {
    return (
      <Page title="Company Settings">
        <Card>
          <CardContent className="py-10">
            <LoadingSpinner />
          </CardContent>
        </Card>
      </Page>
    )
  }

  if (q.isError) {
    return (
      <Page title="Company Settings">
        <Alert variant="destructive">
          <AlertDescription>Error loading company settings.</AlertDescription>
        </Alert>
      </Page>
    )
  }

  const showActions = canEdit || canEditSession
  const SaveChangesButton = showActions ? (
    <div className="mt-4 flex justify-end">
      <Button onClick={save} disabled={!generalDirty || m.isPending}>
        {m.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
        Save changes
      </Button>
    </div>
  ) : null

  const canBilling = hasPermission('billing.view')
  const canSettingsView = hasPermission('tenant.settings.view')
  const canSettingsEdit = hasPermission('tenant.settings.edit')
  const canDelete = hasPermission('workspace.delete')
  const canBusinessInfo = hasPermission('client.intake')
  const triggerCls =
    'gap-2 border data-[state=active]:border-primary data-[state=active]:bg-primary/10 data-[state=active]:text-primary'

  return (
    <Page title="Company Settings" width="wide">
      <Tabs value={tab} onValueChange={(v) => setTab(v as TabId)}>
        <TabsList className="h-auto flex-wrap justify-start gap-1 bg-transparent p-0">
          <TabsTrigger value="general" className={triggerCls}>
            <Building2 className="size-4" /> General
          </TabsTrigger>
          {canSettingsView ? (
            <TabsTrigger value="invoicing" className={triggerCls}>
              <FileText className="size-4" /> Invoicing
            </TabsTrigger>
          ) : null}
          {canSettingsView ? (
            <TabsTrigger value="payments" className={triggerCls}>
              <CreditCard className="size-4" /> Payment Collection
            </TabsTrigger>
          ) : null}
          {canBusinessInfo ? (
            <TabsTrigger value="business-info" className={triggerCls}>
              <ClipboardList className="size-4" /> Business Info
            </TabsTrigger>
          ) : null}
          {canSettingsView ? (
            <TabsTrigger value="posting" className={triggerCls}>
              <Landmark className="size-4" /> Posting Config
            </TabsTrigger>
          ) : null}
          {canBilling ? (
            <TabsTrigger value="subscription" className={triggerCls}>
              <CreditCard className="size-4" /> Subscription &amp; Usage
            </TabsTrigger>
          ) : null}
          {canDelete ? (
            <TabsTrigger value="danger" className={triggerCls}>
              <AlertTriangle className="size-4" /> Danger zone
            </TabsTrigger>
          ) : null}
        </TabsList>

      <TabsContent value="general" className="mt-4 space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Business Details</CardTitle>
          <p className="text-sm text-muted-foreground">
            Appears on invoices and receipts. Each field can be overridden per invoice from the editor.
          </p>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <FormField
            label="Business Name"
            value={businessName}
            onChange={(e) => setBusinessName(e.currentTarget.value)}
            placeholder="Company Name"
            disabled={!canEdit}
          />
          <FormField
            label="Company Type"
            control={
              <Select value={companyType} onValueChange={setCompanyType} disabled={!canEdit}>
                <SelectTrigger>
                  <SelectValue placeholder="Select…" />
                </SelectTrigger>
                <SelectContent>
                  {COMPANY_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            }
          />
          <FormField
            label="Business Phone"
            value={businessPhone}
            onChange={(e) => setBusinessPhone(e.currentTarget.value)}
            placeholder="(555) 123-4567"
            disabled={!canEdit}
          />
          <FormField
            label="Business Email"
            type="email"
            value={businessEmail}
            onChange={(e) => setBusinessEmail(e.currentTarget.value)}
            placeholder="billing@company.com"
            disabled={!canEdit}
          />
          <FormField
            label="Address line 1"
            value={addressLine1}
            onChange={(e) => setAddressLine1(e.currentTarget.value)}
            placeholder="Street address"
            disabled={!canEdit}
            containerClassName="md:col-span-2"
          />
          <FormField
            label="Address line 2"
            value={addressLine2}
            onChange={(e) => setAddressLine2(e.currentTarget.value)}
            placeholder="Suite, building, floor (optional)"
            disabled={!canEdit}
            containerClassName="md:col-span-2"
          />
          <FormField
            label="City"
            value={city}
            onChange={(e) => setCity(e.currentTarget.value)}
            disabled={!canEdit}
          />
          <FormField
            label="Country"
            control={
              <CountryCombobox
                value={country || 'US'}
                onChange={(v) => onCountryChange(v || 'US')}
              />
            }
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Regional Settings</CardTitle>
          <p className="text-sm text-muted-foreground">
            Controls date format, currency, number separators, and fiscal year across the app.
          </p>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-3">
          <FormField
            label="Country"
            control={
              <CountryCombobox
                value={country || null}
                onChange={(v) => onCountryChange(v || '')}
              />
            }
          />
          <FormField
            label="Timezone"
            control={
              <Select value={timezone} onValueChange={setTimezone} disabled={!canEdit}>
                <SelectTrigger>
                  <SelectValue placeholder="Auto (from country)" />
                </SelectTrigger>
                <SelectContent>
                  {timezones.map((tz) => (
                    <SelectItem key={tz} value={tz}>
                      {tz.replace(/_/g, ' ')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            }
          />
          <FormField
            label="Fiscal Year Starts"
            control={
              <Select value={fiscalMonth} onValueChange={setFiscalMonth} disabled={!canEdit}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MONTHS.map((m, i) => (
                    <SelectItem key={m} value={String(i + 1)}>
                      {m}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            }
          />
          {preview ? (
            <div className="rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground md:col-span-3">
              <strong className="text-foreground">Preview:</strong> Date: {preview.date} · Currency:{' '}
              {preview.currency} · Number: {preview.number} · Fiscal year starts: {preview.fiscal}
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Session Settings</CardTitle>
          <p className="text-sm text-muted-foreground">
            Controls how long team members stay signed in before requiring re-authentication.
          </p>
        </CardHeader>
        <CardContent>
          {canEditSession ? (
            <div className="grid gap-4 md:grid-cols-2">
              <FormField
                label="Session Expiry"
                control={
                  <Select value={sessionTimeout} onValueChange={setSessionTimeout}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SESSION_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                }
                hint="Team members will need to log in again after the selected period of inactivity."
              />
            </div>
          ) : (
            <div className="rounded-md border bg-muted/40 p-3 text-sm text-muted-foreground">
              <strong className="text-foreground">Current policy:</strong>{' '}
              {SESSION_LABEL.get(sessionTimeout) ?? sessionTimeout}
              <span className="mt-1 block text-xs">
                Only super admins can change session settings.
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {SaveChangesButton}
      </TabsContent>

      {canSettingsView ? (
        <TabsContent value="invoicing" className="mt-4 space-y-4">
          <InvoicingDefaultsSection />
          <BankAccountsSection />
        </TabsContent>
      ) : null}

      {canSettingsView ? (
        <TabsContent value="payments" className="mt-4">
          <PaymentCollectionSection />
        </TabsContent>
      ) : null}

      {canBusinessInfo ? (
        <TabsContent value="business-info" className="mt-4">
          <BusinessInfoSection />
        </TabsContent>
      ) : null}

      {canSettingsView ? (
        <TabsContent value="posting" className="mt-4">
          <PostingConfigSection canEdit={canSettingsEdit} />
        </TabsContent>
      ) : null}

      {canBilling ? (
        <TabsContent value="subscription" className="mt-4">
          <SubscriptionUsageSection />
        </TabsContent>
      ) : null}

      {canDelete ? (
        <TabsContent value="danger" className="mt-4">
          <DangerZoneSection tenantName={q.data?.tenant?.business_name || q.data?.tenant?.name || 'this workspace'} />
        </TabsContent>
      ) : null}
      </Tabs>
    </Page>
  )
}

/**
 * Danger zone: soft-delete the entire workspace. Only rendered for users
 * with `workspace.delete` (seeded to super_admin only). Requires typing
 * the workspace name to confirm - matches the GitHub "delete repo" UX and
 * prevents accidental clicks. On success, all refresh tokens scoped to
 * this tenant are revoked server-side; we clear the local token and send
 * the user back to login.
 */
function DangerZoneSection({ tenantName }: { tenantName: string }) {
  const { hasPermission } = useAuth()
  const canDelete = hasPermission('workspace.delete')
  // Modal lifecycle has THREE conceptual steps; we model them with two
  // booleans so the user always sees the warning first and can back out
  // before any code is generated:
  //   open        → warning modal is visible
  //   codeSent    → after request-code success, show name + code inputs
  // Closing the modal resets both back to false.
  const [open, setOpen] = useState(false)
  const [codeSent, setCodeSent] = useState(false)
  const [acknowledged, setAcknowledged] = useState(false)
  const [emailHint, setEmailHint] = useState<string | null>(null)
  const [typed, setTyped] = useState('')
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const requestCodeMut = useRequestDeleteWorkspaceCode()
  const deleteMut = useDeleteWorkspace()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { toast } = useToast()

  const close = () => {
    setOpen(false)
    setCodeSent(false)
    setAcknowledged(false)
    setEmailHint(null)
    setTyped('')
    setCode('')
    setError(null)
  }

  const requestCode = async () => {
    setError(null)
    try {
      const res = await requestCodeMut.mutateAsync()
      setEmailHint(res.email || null)
      setCodeSent(true)
    } catch (err) {
      setError(messageFor(err, 'Could not send the confirmation code. Please try again.'))
    }
  }

  const onConfirm = async () => {
    setError(null)
    if (typed.trim() !== tenantName) {
      setError(`Type "${tenantName}" exactly to confirm.`)
      return
    }
    if (code.trim().length !== 6) {
      setError('Enter the 6-digit code we emailed you.')
      return
    }
    try {
      await deleteMut.mutateAsync({ name: typed.trim(), verificationCode: code.trim() })
      // Wipe local state: token, query cache, per-tenant localStorage keys.
      tokenManager.clear()
      qc.clear()
      try {
        ['userCountry', 'userTimezone', 'userFiscalStartMonth'].forEach((k) => localStorage.removeItem(k))
      } catch { /* ignore */ }
      toast({ title: 'Workspace deleted', description: 'All members have been signed out.' })
      navigate('/login', { replace: true })
    } catch (err) {
      setError(messageFor(err, 'Failed to delete workspace.'))
    }
  }

  // Server-side already enforces `workspace.delete` (mig 057 + the
  // `requirePermission` middleware on the route). Hiding the UI here is
  // a usability layer, not a security layer - if a non-permitted user
  // somehow opens the modal, the API call will reject with 403.
  if (!canDelete) {
    return (
      <Card className="border-destructive/40">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="size-5" /> Danger zone
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Only workspace owners can delete this workspace. Ask your super-admin if this needs to happen.
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <>
      <Card className="border-destructive/40">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="size-5" /> Danger zone
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Deleting this workspace is <span className="font-semibold">permanent</span>. All statements, reports,
            journal entries, budgets, team memberships, API keys, and subscription state for this workspace will
            be removed. Every member of this workspace will be signed out.
          </p>
          <p className="text-sm text-muted-foreground">
            Your other workspaces (if any) are not affected - they have their own data, billing, and team.
          </p>
          <Button variant="danger" className="gap-2" onClick={() => setOpen(true)}>
            <Trash2 className="size-4" /> Delete this workspace
          </Button>
        </CardContent>
      </Card>

      <Modal
        open={open}
        onOpenChange={(next) => { if (!next) close() }}
        title={codeSent ? `Confirm deleting "${tenantName}"` : 'Delete this workspace?'}
        description={
          codeSent
            ? 'Type the workspace name AND the 6-digit code we emailed you.'
            : 'This cannot be undone. Read the warning carefully before continuing.'
        }
        className="sm:max-w-lg"
        footer={
          codeSent ? (
            <>
              <Button variant="secondary" onClick={close} disabled={deleteMut.isPending}>Cancel</Button>
              <Button variant="danger" onClick={onConfirm} disabled={deleteMut.isPending}>
                {deleteMut.isPending ? <Loader2 className="size-4 animate-spin" /> : <Trash2 className="size-4" />}
                Delete workspace permanently
              </Button>
            </>
          ) : (
            <>
              <Button variant="secondary" onClick={close} disabled={requestCodeMut.isPending}>Cancel</Button>
              <Button
                variant="danger"
                onClick={requestCode}
                disabled={!acknowledged || requestCodeMut.isPending}
              >
                {requestCodeMut.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
                Email me a confirmation code
              </Button>
            </>
          )
        }
      >
        {codeSent ? (
          <div className="space-y-3">
            {emailHint ? (
              <Alert>
                <AlertDescription>
                  We sent a 6-digit code to <span className="font-mono">{emailHint}</span>. It expires in 10 minutes.
                </AlertDescription>
              </Alert>
            ) : null}
            <FormField
              label={
                <>
                  Type <span className="font-mono font-semibold">{tenantName}</span> to confirm
                </>
              }
              value={typed}
              onChange={(e) => setTyped(e.currentTarget.value)}
              placeholder={tenantName}
              autoFocus
            />
            <FormField
              label="6-digit confirmation code"
              control={
                <Input
                  value={code}
                  onChange={(e) => setCode(e.currentTarget.value.replace(/\D/g, ''))}
                  maxLength={6}
                  inputMode="numeric"
                  placeholder="123456"
                  className="text-center font-mono text-xl tracking-[10px]"
                />
              }
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-xs"
              onClick={requestCode}
              disabled={requestCodeMut.isPending}
            >
              Didn&apos;t receive it? Resend code
            </Button>
            {error ? <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert> : null}
          </div>
        ) : (
          <div className="space-y-3">
            <Alert variant="destructive">
              <AlertTriangle className="size-4" />
              <AlertDescription>
                <p className="font-semibold">This action is permanent and cannot be undone.</p>
                <ul className="ml-4 mt-1 list-disc space-y-0.5 text-xs">
                  <li>Every member of <span className="font-semibold">{tenantName}</span> will be signed out immediately.</li>
                  <li>All statements, reports, journal entries, budgets, invoices and bills will be deleted forever.</li>
                  <li>Subscription state, API keys, integrations and audit history are removed.</li>
                  <li>We cannot bring any of this back, even if you contact support.</li>
                </ul>
              </AlertDescription>
            </Alert>
            <label className="flex items-start gap-2 text-sm">
              <Checkbox
                checked={acknowledged}
                onCheckedChange={(v) => setAcknowledged(v === true)}
                className="mt-0.5"
              />
              <span>
                I understand that deleting <span className="font-semibold">{tenantName}</span> is permanent
                and irreversible.
              </span>
            </label>
            {error ? <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert> : null}
          </div>
        )}
      </Modal>
    </>
  )
}

function InvoicingDefaultsSection() {
  const { hasPermission } = useAuth()
  const canEdit = hasPermission('tenant.settings.edit')
  const { toast } = useToast()
  const q = useInvoicingSettings()
  const m = useUpdateInvoicingSettings()

  const [prefix, setPrefix] = useState('INV-')
  const [padding, setPadding] = useState('4')
  const [nextNumber, setNextNumber] = useState('1')
  const [taxRate, setTaxRate] = useState('0')
  const [remittance, setRemittance] = useState('')
  const [reminderEnabled, setReminderEnabled] = useState(false)
  const [reminderCadence, setReminderCadence] = useState('7, 14, 30')
  const [reminderMaxCount, setReminderMaxCount] = useState('3')

  useEffect(() => {
    const s = q.data?.settings
    if (!s) return
    setPrefix(s.number_prefix ?? 'INV-')
    setPadding(String(s.number_padding ?? 4))
    setNextNumber(String(s.next_number ?? 1))
    setTaxRate(String(s.default_tax_rate_pct ?? 0))
    setRemittance(s.remittance_instructions ?? '')
    setReminderEnabled(!!s.reminder_enabled)
    setReminderCadence(Array.isArray(s.reminder_cadence_days) && s.reminder_cadence_days.length > 0
      ? s.reminder_cadence_days.join(', ')
      : '7, 14, 30')
    setReminderMaxCount(String(s.reminder_max_count ?? 3))
  }, [q.data])

  function parseCadence(raw: string): number[] {
    return raw
      .split(/[,\s]+/)
      .map((s) => parseInt(s, 10))
      .filter((n) => Number.isFinite(n) && n >= 0 && n <= 365)
  }

  const form = useMemo(
    () => ({ prefix, padding, nextNumber, taxRate, remittance, reminderEnabled, reminderCadence, reminderMaxCount }),
    [prefix, padding, nextNumber, taxRate, remittance, reminderEnabled, reminderCadence, reminderMaxCount],
  )
  const baseline = useMemo(() => {
    const s = q.data?.settings
    if (!s) return null
    return {
      prefix: s.number_prefix ?? 'INV-',
      padding: String(s.number_padding ?? 4),
      nextNumber: String(s.next_number ?? 1),
      taxRate: String(s.default_tax_rate_pct ?? 0),
      remittance: s.remittance_instructions ?? '',
      reminderEnabled: !!s.reminder_enabled,
      reminderCadence: Array.isArray(s.reminder_cadence_days) && s.reminder_cadence_days.length > 0
        ? s.reminder_cadence_days.join(', ')
        : '7, 14, 30',
      reminderMaxCount: String(s.reminder_max_count ?? 3),
    }
  }, [q.data])
  const dirty = useDirty(form, baseline)

  const preview = `${prefix || 'INV-'}${String(Number(nextNumber) || 1).padStart(Math.max(0, Math.min(12, Number(padding) || 0)), '0')}`

  async function save() {
    try {
      const cadence = parseCadence(reminderCadence)
      if (reminderEnabled && cadence.length === 0) {
        toast({ variant: 'destructive', title: 'Cadence needs at least one day (e.g. 7, 14, 30).' })
        return
      }
      await m.mutateAsync({
        number_prefix: prefix,
        number_padding: Number(padding) || 0,
        next_number: Number(nextNumber) || 1,
        default_tax_rate_pct: Number(taxRate) || 0,
        remittance_instructions: remittance || '',
        reminder_enabled: reminderEnabled,
        reminder_cadence_days: cadence.length > 0 ? cadence : [7, 14, 30],
        reminder_max_count: Math.max(0, Math.min(20, Number(reminderMaxCount) || 0)),
      })
      toast({ title: 'Invoicing defaults saved.' })
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Failed to save invoicing defaults',
        description: messageFor(err, ''),
      })
    }
  }

  if (q.isLoading) {
    return (
      <Card>
        <CardHeader><CardTitle>Invoicing defaults</CardTitle></CardHeader>
        <CardContent className="py-6"><LoadingSpinner /></CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Invoicing defaults</CardTitle>
        <p className="text-sm text-muted-foreground">
          Tenant-wide defaults used by every new invoice. Per-invoice overrides in the editor
          (logo, sender name, tax rate) take precedence - these are the fallback.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 md:grid-cols-3">
          <FormField
            label="Invoice number prefix"
            value={prefix}
            onChange={(e) => setPrefix(e.currentTarget.value)}
            disabled={!canEdit}
            placeholder="INV-"
            hint="Shows on every new invoice number."
          />
          <FormField
            label="Padding width"
            type="number"
            min={0}
            max={12}
            value={padding}
            onChange={(e) => setPadding(e.currentTarget.value)}
            disabled={!canEdit}
            hint="Zero-pads the counter (e.g. 4 → 0001)."
          />
          <FormField
            label="Next number"
            type="number"
            min={1}
            value={nextNumber}
            onChange={(e) => setNextNumber(e.currentTarget.value)}
            disabled={!canEdit}
            hint="Can only advance - auditor-safe."
          />
        </div>
        <div className="rounded-md border bg-muted/40 p-3 text-sm">
          <span className="text-muted-foreground">Next invoice number: </span>
          <span className="font-mono font-semibold">{preview}</span>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <FormField
            label="Default tax rate (%)"
            type="number"
            step="0.01"
            min={0}
            max={100}
            value={taxRate}
            onChange={(e) => setTaxRate(e.currentTarget.value)}
            disabled={!canEdit}
            hint="Applied as default to new invoice lines. Editable per line."
          />
          <FormField
            label="Remittance instructions"
            control={
              <textarea
                className="min-h-[72px] w-full rounded-md border bg-card px-3 py-2 text-sm"
                value={remittance}
                onChange={(e) => setRemittance(e.currentTarget.value)}
                disabled={!canEdit}
                placeholder="Wire to… / Mail checks to…"
              />
            }
            hint="Appears on the PDF + hosted pay page footer."
          />
        </div>
        <div className="rounded-md border p-3">
          <div className="mb-3">
            <div className="text-sm font-semibold">Reminder cadence</div>
            <p className="text-xs text-muted-foreground">
              When reminders are enabled, the overnight job sends an email reminder at each cadence day past the invoice due date (up to the max count). A new pay-link is issued on every reminder.
            </p>
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            <FormField
              label="Enabled"
              control={
                <div className="flex h-10 items-center gap-2 rounded-md border bg-card px-3">
                  <Checkbox
                    checked={reminderEnabled}
                    onCheckedChange={(v) => setReminderEnabled(v === true)}
                    disabled={!canEdit}
                  />
                  <span className="text-sm text-muted-foreground">
                    {reminderEnabled ? 'Reminders will be sent' : 'Reminders paused'}
                  </span>
                </div>
              }
            />
            <FormField
              label="Cadence days (after due)"
              value={reminderCadence}
              onChange={(e) => setReminderCadence(e.currentTarget.value)}
              disabled={!canEdit}
              placeholder="7, 14, 30"
              hint="Comma-separated days. Sorted ascending on save."
            />
            <FormField
              label="Max reminders per invoice"
              type="number"
              min={0}
              max={20}
              value={reminderMaxCount}
              onChange={(e) => setReminderMaxCount(e.currentTarget.value)}
              disabled={!canEdit}
              hint="Capped to avoid spamming customers."
            />
          </div>
        </div>
        {canEdit ? (
          <div className="flex justify-end">
            <Button onClick={save} disabled={!dirty || m.isPending}>
              {m.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
              Save invoicing defaults
            </Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  )
}

function SubscriptionUsageSection() {
  const subQ = useBillingSubscription()
  const usageQ = useBillingUsage()

  const sub = subQ.data?.subscription
  const usage = usageQ.data?.usage

  const planLabel = ((sub?.basePlan ?? 'trial') + ' plan').toUpperCase()
  const status = sub?.status ?? 'trialing'

  return (
    <Card>
      <CardHeader>
        <CardTitle>Subscription &amp; Usage</CardTitle>
        <p className="text-sm text-muted-foreground">
          Your tenant&rsquo;s plan and shared usage quotas. Every member of this tenant
          consumes from the same pool. Team invitations are free on every plan.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border bg-muted/40 p-4">
          <div className="flex flex-wrap items-center gap-2">
            <Badge className="bg-primary-gradient text-primary-foreground">{planLabel}</Badge>
            <Badge variant={status === 'active' ? 'success' : 'secondary'}>{status}</Badge>
          </div>
          <Button asChild variant="primary" size="sm">
            <Link to="/subscriptions">Manage plan</Link>
          </Button>
        </div>

        {usageQ.isLoading ? (
          <LoadingSpinner />
        ) : usage ? (
          <div className="grid gap-4 md:grid-cols-2">
            <UsageBar
              label="Tokens used"
              used={Number(usage.ai_tokens_used ?? 0)}
              max={Number(usage.ai_tokens_limit ?? 0)}
              note="Shared tenant pool - consumed by Smart Chat, AI Guide, Budget AI, Statement Classification, and Plaid categorization."
            />
            <UsageBar
              label="Transactions processed"
              used={Number(usage.transactions_used ?? 0)}
              max={Number(usage.transactions_limit ?? 0)}
              note="Monthly transaction limit - shared across every member of your tenant."
            />
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            Usage is not available for your current plan.
          </p>
        )}
      </CardContent>
    </Card>
  )
}

function UsageBar({
  label,
  used,
  max,
  note,
}: {
  label: string
  used: number
  max: number
  note?: string
}) {
  const unlimited = max === -1 || max === 0
  const percent = unlimited ? 100 : Math.min((used / max) * 100, 100)
  const barColor =
    percent > 90 ? 'bg-destructive' : percent > 75 ? 'bg-warning' : 'bg-primary-gradient'
  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between gap-2 text-sm">
        <span className="font-medium">{label}</span>
        <span className="font-mono text-xs text-muted-foreground">
          {used.toLocaleString()} / {unlimited ? 'Unlimited' : max.toLocaleString()}
        </span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
        <div
          className={cn('h-full transition-all', unlimited ? 'bg-success' : barColor)}
          style={{ width: `${percent}%` }}
        />
      </div>
      {note ? <p className="text-[11px] text-muted-foreground">{note}</p> : null}
    </div>
  )
}

function messageFor(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.body && typeof err.body === 'object' && 'error' in err.body) {
    const m = (err.body as { error: unknown }).error
    if (typeof m === 'string' && m) return m
  }
  if (err instanceof Error && err.message) return err.message
  return fallback
}

function blankBankPayload(): TenantBankAccountPayload {
  return {
    label: '',
    bank_name: '',
    account_name: '',
    account_number: '',
    sort_code: '',
    routing_number: '',
    iban: '',
    swift: '',
    is_default: false,
  }
}

function BankAccountsSection() {
  const { hasPermission } = useAuth()
  const canEdit = hasPermission('tenant.settings.edit')
  const { toast } = useToast()
  const q = useTenantBankAccounts()
  const createMut = useCreateTenantBankAccount()
  const updateMut = useUpdateTenantBankAccount()
  const deleteMut = useDeleteTenantBankAccount()

  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<TenantBankAccount | null>(null)
  const [form, setForm] = useState<TenantBankAccountPayload>(blankBankPayload())
  const [baseline, setBaseline] = useState<TenantBankAccountPayload | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<TenantBankAccount | null>(null)

  const accounts = q.data?.bank_accounts ?? []
  const dirty = useDirty(form, baseline)

  function openNew() {
    setEditing(null)
    setForm({ ...blankBankPayload(), is_default: accounts.length === 0 })
    setBaseline(null)
    setOpen(true)
  }

  function openEdit(acc: TenantBankAccount) {
    setEditing(acc)
    const next: TenantBankAccountPayload = {
      label: acc.label,
      bank_name: acc.bank_name ?? '',
      account_name: acc.account_name ?? '',
      account_number: acc.account_number ?? '',
      sort_code: acc.sort_code ?? '',
      routing_number: acc.routing_number ?? '',
      iban: acc.iban ?? '',
      swift: acc.swift ?? '',
      is_default: acc.is_default,
    }
    setForm(next)
    setBaseline(next)
    setOpen(true)
  }

  async function onSave() {
    if (!form.label?.trim()) {
      toast({ title: 'Label is required', variant: 'destructive' })
      return
    }
    try {
      if (editing) {
        await updateMut.mutateAsync({ id: editing.id, body: form })
        toast({ title: 'Bank account updated' })
      } else {
        await createMut.mutateAsync(form)
        toast({ title: 'Bank account added' })
      }
      setOpen(false)
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Failed to save bank account',
        description: messageFor(err, ''),
      })
    }
  }

  async function onDelete() {
    if (!confirmDelete) return
    try {
      await deleteMut.mutateAsync(confirmDelete.id)
      toast({ title: 'Bank account removed' })
      setConfirmDelete(null)
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Failed to delete bank account',
        description: messageFor(err, ''),
      })
    }
  }

  async function makeDefault(acc: TenantBankAccount) {
    try {
      await updateMut.mutateAsync({
        id: acc.id,
        body: {
          label: acc.label,
          bank_name: acc.bank_name,
          account_name: acc.account_name,
          account_number: acc.account_number,
          sort_code: acc.sort_code,
          routing_number: acc.routing_number,
          iban: acc.iban,
          swift: acc.swift,
          is_default: true,
        },
      })
      toast({ title: `${acc.label} is now the default bank account` })
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Failed to update default',
        description: messageFor(err, ''),
      })
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Bank Accounts</CardTitle>
        <p className="text-sm text-muted-foreground">
          Add the bank details you want on invoices. The default account is auto-filled into the
          invoice editor&apos;s &quot;Bank details&quot; section; users can still pick a different one or
          clear it per invoice.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {q.isLoading ? (
          <LoadingSpinner />
        ) : accounts.length === 0 ? (
          <div className="rounded-md border bg-muted/40 p-4 text-sm text-muted-foreground">
            No bank accounts yet.{' '}
            {canEdit
              ? 'Add one below to have it automatically appear on new invoices.'
              : 'Ask an admin to add a bank account.'}
          </div>
        ) : (
          <ul className="space-y-2">
            {accounts.map((acc) => (
              <li key={acc.id} className="rounded-md border p-3">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{acc.label}</span>
                      {acc.is_default ? <Badge variant="success">default</Badge> : null}
                    </div>
                    <div className="mt-1 space-y-0.5 text-xs text-muted-foreground">
                      {acc.bank_name ? <div>{acc.bank_name}</div> : null}
                      {acc.account_name ? <div>Account Name: {acc.account_name}</div> : null}
                      {acc.account_number ? <div>Account Number: {acc.account_number}</div> : null}
                      {acc.sort_code ? <div>Sort Code: {acc.sort_code}</div> : null}
                      {acc.routing_number ? <div>Routing: {acc.routing_number}</div> : null}
                      {acc.iban ? <div>IBAN: {acc.iban}</div> : null}
                      {acc.swift ? <div>SWIFT: {acc.swift}</div> : null}
                    </div>
                  </div>
                  {canEdit ? (
                    <div className="flex flex-wrap items-center gap-1">
                      {!acc.is_default ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => makeDefault(acc)}
                          disabled={updateMut.isPending}
                        >
                          <CheckCircle2 className="mr-1 size-4" /> Make default
                        </Button>
                      ) : null}
                      <Button size="icon" variant="ghost" className="size-7" onClick={() => openEdit(acc)}>
                        <Pencil className="size-3.5" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="size-7"
                        onClick={() => setConfirmDelete(acc)}
                      >
                        <Trash2 className="size-3.5 text-destructive" />
                      </Button>
                    </div>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}

        {canEdit ? (
          <div>
            <Button variant="secondary" onClick={openNew}>
              <Plus className="mr-1 size-4" /> Add bank account
            </Button>
          </div>
        ) : null}
      </CardContent>

      <Modal
        open={open}
        onOpenChange={setOpen}
        title={editing ? 'Edit bank account' : 'Add bank account'}
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={onSave} disabled={!dirty || createMut.isPending || updateMut.isPending}>
              {(createMut.isPending || updateMut.isPending) ? <Loader2 className="size-4 animate-spin" /> : null}
              {editing ? 'Save changes' : 'Add account'}
            </Button>
          </>
        }
      >
        <div className="grid gap-3 md:grid-cols-2">
          <FormField
            label="Label"
            required
            value={form.label}
            onChange={(e) => setForm((f) => ({ ...f, label: e.currentTarget.value }))}
            placeholder="e.g. Operations, USD"
            containerClassName="md:col-span-2"
          />
          <FormField
            label="Bank Name"
            value={form.bank_name || ''}
            onChange={(e) => setForm((f) => ({ ...f, bank_name: e.currentTarget.value }))}
            placeholder="Chase Bank, N.A."
            containerClassName="md:col-span-2"
          />
          <FormField
            label="Account Name"
            value={form.account_name || ''}
            onChange={(e) => setForm((f) => ({ ...f, account_name: e.currentTarget.value }))}
            placeholder="Acme Holdings Inc."
          />
          <FormField
            label="Account Number"
            value={form.account_number || ''}
            onChange={(e) => setForm((f) => ({ ...f, account_number: e.currentTarget.value }))}
            placeholder="000123456789"
          />
          <FormField
            label="Sort Code"
            value={form.sort_code || ''}
            onChange={(e) => setForm((f) => ({ ...f, sort_code: e.currentTarget.value }))}
            placeholder="Leave blank if not applicable"
          />
          <FormField
            label="Routing Number"
            value={form.routing_number || ''}
            onChange={(e) => setForm((f) => ({ ...f, routing_number: e.currentTarget.value }))}
            placeholder="021000021"
          />
          <FormField
            label="IBAN"
            value={form.iban || ''}
            onChange={(e) => setForm((f) => ({ ...f, iban: e.currentTarget.value }))}
            placeholder="International only - leave blank for US"
            containerClassName="md:col-span-2"
          />
          <FormField
            label="SWIFT / BIC"
            value={form.swift || ''}
            onChange={(e) => setForm((f) => ({ ...f, swift: e.currentTarget.value }))}
            placeholder="CHASUS33"
          />
          <FormField
            label="Default account"
            control={
              <div className="flex h-10 items-center gap-2 rounded-md border bg-card px-3">
                <Checkbox
                  checked={!!form.is_default}
                  onCheckedChange={(v) => setForm((f) => ({ ...f, is_default: v === true }))}
                />
                <span className="text-sm text-muted-foreground">
                  Auto-fill into new invoices
                </span>
              </div>
            }
          />
        </div>
      </Modal>

      <Modal.Confirm
        open={confirmDelete !== null}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
        title="Remove bank account?"
        body={`Remove "${confirmDelete?.label ?? ''}" from the bank accounts used on invoices? Existing invoices keep their saved bank details.`}
        confirmLabel="Remove"
        confirmVariant="danger"
        onConfirm={onDelete}
        busy={deleteMut.isPending}
      />
    </Card>
  )
}

/**
 * Posting Config section - per-source manual/auto toggle for everything
 * that posts to the GL (bank, accruals, JE, depreciation, prepaid, loan,
 * invoices, bills). Defaults: schedules = auto, everything else =
 * manual. When a source is on 'auto', new entries post immediately when
 * they're created; on 'manual', they queue up for the Post Now banner
 * on each page.
 *
 * Pure CRUD - the actual gating logic lives at the call sites of each
 * domain (e.g. invoice-service decides whether to auto-post on issue
 * by reading posting_config.invoices_mode).
 */
function PostingConfigSection({ canEdit }: { canEdit: boolean }) {
  const q = usePostingConfig()
  const m = useUpdatePostingConfig()
  const { toast } = useToast()

  if (q.isLoading) {
    return (
      <Card>
        <CardContent className="py-10"><LoadingSpinner /></CardContent>
      </Card>
    )
  }

  const cfg = q.data?.config
  if (!cfg) return null

  const onToggle = async (
    source: keyof typeof POSTING_SOURCE_LABELS,
    next: 'manual' | 'auto',
  ) => {
    try {
      await m.mutateAsync({ [source]: next } as Partial<typeof cfg>)
      toast({
        title: 'Posting mode updated.',
        description: `${POSTING_SOURCE_LABELS[source]} → ${next === 'auto' ? 'Auto-post' : 'Manual'}`,
      })
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Could not save posting mode',
        description: err instanceof Error ? err.message : 'Please try again.',
      })
    }
  }

  const sources = (Object.keys(POSTING_SOURCE_LABELS) as Array<keyof typeof POSTING_SOURCE_LABELS>)

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Landmark className="size-5" /> Posting Config
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Choose how each source lands on the General Ledger.{' '}
          <strong>Manual</strong> queues entries for review; click &quot;Post Now&quot; on the source&apos;s
          page to push them to the GL.{' '}
          <strong>Auto-post</strong> writes to the GL the moment the entry is created.
          Allocation schedules default to auto-post; everything else defaults to manual.
        </p>
      </CardHeader>
      <CardContent className="space-y-2">
        {sources.map((src) => {
          const mode = cfg[src]
          const isAuto = mode === 'auto'
          return (
            <div
              key={src}
              className="flex flex-wrap items-center justify-between gap-3 rounded-md border p-3"
            >
              <div className="min-w-0">
                <div className="text-sm font-medium">{POSTING_SOURCE_LABELS[src]}</div>
                <div className="text-xs text-muted-foreground">
                  Currently: <strong>{isAuto ? 'Auto-post' : 'Manual'}</strong>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant={isAuto ? 'outline' : 'primary'}
                  // Why: only disable for permission/loading reasons - never
                  // for "this is the active option". Disabling the selected
                  // button applies `disabled:opacity-50`, which washes the
                  // brand colour to a faded tint. Clicking the active option
                  // is a harmless no-op so we just guard the handler.
                  onClick={() => { if (isAuto) onToggle(src, 'manual') }}
                  disabled={!canEdit || m.isPending}
                  aria-pressed={!isAuto}
                >
                  Manual
                </Button>
                <Button
                  size="sm"
                  variant={isAuto ? 'primary' : 'outline'}
                  onClick={() => { if (!isAuto) onToggle(src, 'auto') }}
                  disabled={!canEdit || m.isPending}
                  aria-pressed={isAuto}
                >
                  Auto-post
                </Button>
              </div>
            </div>
          )
        })}
      </CardContent>
    </Card>
  )
}

/**
 * Payment Collection mode picker (Feature 2). Two paths, no fallback:
 *
 *   - Stripe Connect Express → tenant onboards their own Stripe
 *     account. Money settles directly into their bank.
 *   - Bokapsys-managed → Bokapsys collects on its Stripe account and
 *     pays the tenant out later (true MoR / clearing model).
 *
 * The chosen mode is required before the hosted invoice pay page can
 * create a Checkout Session. Until set, the pay page shows a friendly
 * "merchant not set up yet" state.
 *
 * The Connect onboarding flow is hosted by Stripe; we redirect the
 * tenant to a single-use account link, they fill in the form, then
 * Stripe sends them back to /company?stripe=onboarded - at which
 * point we sync status from Stripe and update local mirror.
 */
function PaymentCollectionSection() {
  const { hasPermission } = useAuth()
  const canEdit = hasPermission('tenant.settings.edit')
  const { toast } = useToast()
  const navigate = useNavigate()

  const statusQ = useStripeConnectStatus()
  const setMode = useSetStripeConnectMode()
  const startOnboarding = useStartStripeOnboarding()
  const refreshLink = useRefreshStripeOnboardingLink()
  const syncStatus = useSyncStripeConnect()
  const openDashboard = useOpenStripeDashboard()
  const disconnect = useDisconnectStripe()

  const [country, setCountry] = useState<string>('')
  const [showCountryPicker, setShowCountryPicker] = useState(false)
  const [confirmDisconnect, setConfirmDisconnect] = useState(false)
  const countriesQ = useStripeConnectCountries(showCountryPicker)

  const status = statusQ.data
  const mode = status?.mode ?? null
  const isConnect = mode === 'connect_express'
  const isManaged = mode === 'bokapsys_managed'
  const ready = isConnect && status?.chargesEnabled

  // Stripe sends the tenant back to /company?stripe=onboarded after the
  // hosted onboarding form. Sync once on mount when that param is set.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const params = new URLSearchParams(window.location.search)
    if (params.get('stripe') === 'onboarded') {
      syncStatus.mutateAsync().then(() => {
        toast({ title: 'Stripe account synced', description: 'Onboarding details refreshed.' })
      }).catch(() => {})
      // Strip the marker so a refresh doesn't re-trigger.
      params.delete('stripe')
      const nextUrl = window.location.pathname + (params.toString() ? `?${params.toString()}` : '')
      navigate(nextUrl, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // If the tenant already has a Stripe account from a previous
  // onboarding (status.stripeAccountId is set), switching back to
  // Connect mode does NOT re-trigger onboarding - we just flip the
  // mode flag and the existing connection is reused. Only first-time
  // tenants see the country picker.
  const onPickConnect = async () => {
    if (!canEdit) return
    if (!status?.stripeAccountId) {
      setShowCountryPicker(true)
      return
    }
    try {
      const r = await setMode.mutateAsync('connect_express')
      if (r.nextAction === 'resume_onboarding') {
        toast({
          title: 'Switched back to Stripe Connect.',
          description: 'Onboarding wasn\'t finished - resume it below.',
        })
      } else {
        toast({ title: 'Switched back to Stripe Connect.' })
      }
      // Re-sync status so the UI reflects charges_enabled etc.
      syncStatus.mutateAsync().catch(() => {})
    } catch (err) {
      toast({ variant: 'destructive', title: 'Failed to set mode', description: messageFor(err, '') })
    }
  }

  const onPickManaged = async () => {
    if (!canEdit) return
    try {
      await setMode.mutateAsync('bokapsys_managed')
      toast({
        title: 'Switched to Bokapsys-managed payments.',
        description: 'Configure your payout bank account below so we know where to send funds.',
      })
    } catch (err) {
      toast({ variant: 'destructive', title: 'Failed to set mode', description: messageFor(err, '') })
    }
  }

  // Why: open Stripe-hosted forms in a new tab so the tenant can keep
  // their place in Bokapsys. Fall back to same-tab navigation if the
  // browser's pop-up blocker refuses (returns null) - the onboarding
  // flow still works that way, the user just navigates back manually.
  const openStripeUrl = (url: string) => {
    const w = window.open(url, '_blank', 'noopener,noreferrer')
    if (!w) window.location.assign(url)
    else setShowCountryPicker(false)
  }

  const onStartOnboarding = async () => {
    if (!country) {
      toast({ variant: 'destructive', title: 'Pick a country first.' })
      return
    }
    try {
      const res = await startOnboarding.mutateAsync(country)
      if (!res?.url) {
        throw new Error('Stripe did not return an onboarding URL.')
      }
      openStripeUrl(res.url)
    } catch (err) {
      // Why: surface the server's specific message (e.g. "Stripe is not
      // configured", "Country XX is not supported", or a permission
      // error) instead of a generic "Onboarding failed" so the tenant
      // can act on it.
      const detail = messageFor(err, 'Please try again or contact support.')
      toast({ variant: 'destructive', title: 'Onboarding failed', description: detail })
    }
  }

  const onResumeOnboarding = async () => {
    try {
      const res = await refreshLink.mutateAsync()
      if (!res?.url) {
        throw new Error('Stripe did not return an onboarding URL.')
      }
      openStripeUrl(res.url)
    } catch (err) {
      toast({ variant: 'destructive', title: 'Could not get a fresh onboarding link', description: messageFor(err, '') })
    }
  }

  const onOpenDashboard = async () => {
    try {
      const res = await openDashboard.mutateAsync()
      window.open(res.url, '_blank', 'noopener,noreferrer')
    } catch (err) {
      toast({ variant: 'destructive', title: 'Could not open Stripe dashboard', description: messageFor(err, '') })
    }
  }

  const onDisconnect = async () => {
    try {
      await disconnect.mutateAsync()
      setConfirmDisconnect(false)
      toast({ title: 'Stripe disconnected' })
    } catch (err) {
      toast({ variant: 'destructive', title: 'Disconnect failed', description: messageFor(err, '') })
    }
  }

  if (statusQ.isLoading) {
    return (
      <Card>
        <CardContent className="py-10"><LoadingSpinner /></CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="size-5" /> Payment Collection
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <p className="text-sm text-muted-foreground">
          Choose how invoice payments are collected. You can switch later.
        </p>

        <div className="grid gap-3 md:grid-cols-2">
          {/* Card 1: Stripe Connect */}
          <div
            className={cn(
              'rounded-md border p-4 transition-colors',
              isConnect ? 'border-primary bg-primary/5' : 'border-border',
            )}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2">
                <Wallet className="size-5 text-primary" />
                <h3 className="text-base font-semibold">Stripe Connect (Express)</h3>
              </div>
              {isConnect ? <Badge variant="success">Selected</Badge> : null}
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              Money lands directly in your own bank account. You manage payouts and tax forms in
              your Stripe Express dashboard. 0% platform fee.
            </p>
            <ul className="mt-3 space-y-1 text-xs text-muted-foreground">
              <li>• Settles to your bank, usually next business day</li>
              <li>• Stripe processing fees apply (set by Stripe)</li>
              <li>• You see a Stripe dashboard with full payout history</li>
            </ul>
            {!isConnect ? (
              <Button
                className="mt-3"
                onClick={onPickConnect}
                disabled={!canEdit}
              >
                Use Stripe Connect
              </Button>
            ) : null}
          </div>

          {/* Card 2: Bokapsys-managed */}
          <div
            className={cn(
              'rounded-md border p-4 transition-colors',
              isManaged ? 'border-primary bg-primary/5' : 'border-border',
            )}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2">
                <Building2 className="size-5 text-primary" />
                <h3 className="text-base font-semibold">Bokapsys-managed</h3>
              </div>
              {isManaged ? <Badge variant="success">Selected</Badge> : null}
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              Bokapsys collects on its Stripe account and pays you out separately on a
              schedule. Use this if you don&apos;t want to set up Stripe yourself.
            </p>
            <ul className="mt-3 space-y-1 text-xs text-muted-foreground">
              <li>• No Stripe setup required from you</li>
              <li>• Bokapsys settles to your bank on a recurring schedule</li>
              <li>• Statements + tax forms come from Bokapsys</li>
            </ul>
            {!isManaged ? (
              <Button
                className="mt-3"
                variant="secondary"
                onClick={onPickManaged}
                disabled={!canEdit || setMode.isPending}
              >
                {setMode.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
                Use Bokapsys-managed
              </Button>
            ) : null}
          </div>
        </div>

        {/* Connect-mode status detail */}
        {isConnect ? (
          <ConnectStatusBlock
            status={status}
            ready={!!ready}
            canEdit={canEdit}
            syncing={syncStatus.isPending}
            onSync={() => syncStatus.mutateAsync().catch(() => {})}
            onResume={onResumeOnboarding}
            onDashboard={onOpenDashboard}
            onDisconnect={() => setConfirmDisconnect(true)}
            resuming={refreshLink.isPending}
          />
        ) : null}

        {/* Managed-mode: tenant tells Bokapsys where to send payouts. */}
        {isManaged ? (
          <PayoutDestinationBlock canEdit={canEdit} />
        ) : null}

        {/* Mode-agnostic: link to the unified payments dashboard. */}
        {(isConnect || isManaged) ? (
          <Alert>
            <Wallet className="size-4" />
            <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
              <span>
                See your live balance, recent payments, and payout history on the
                Payments dashboard.
              </span>
              <Button asChild size="sm" variant="secondary">
                <Link to="/invoicing/payments">Open dashboard <ArrowUpRight className="size-3" /></Link>
              </Button>
            </AlertDescription>
          </Alert>
        ) : null}

        {/* Country picker for first-time Connect onboarding */}
        <Modal
          open={showCountryPicker}
          onOpenChange={setShowCountryPicker}
          title="Where is your business based?"
        >
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Stripe needs your country to show the right onboarding form.
            </p>
            <FormField
              id="stripe-country"
              label="Country"
              control={
                <Combobox<string>
                  value={country || null}
                  onChange={(v) => setCountry(v ? String(v) : '')}
                  // Why: pair the ISO code with a flag + full country name so
                  // the picker reads the same as the AR/AP customer/vendor
                  // pickers - easier to scan than a list of two-letter codes.
                  options={(countriesQ.data?.countries ?? []).map((c) => ({
                    value: c,
                    label: COUNTRY_NAMES[c as keyof typeof COUNTRY_NAMES] ?? c,
                    description: `${c} · ${COUNTRY_LOCALE_MAP[c as keyof typeof COUNTRY_LOCALE_MAP]?.currency ?? ''}`,
                    icon: <FlagIcon code={c} />,
                  }))}
                  placeholder={countriesQ.isLoading ? 'Loading…' : 'Pick a country'}
                  disabled={countriesQ.isLoading}
                  emptyMessage="No countries matched."
                />
              }
            />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setShowCountryPicker(false)}>Cancel</Button>
              <Button
                onClick={onStartOnboarding}
                disabled={!country || startOnboarding.isPending}
              >
                {startOnboarding.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
                Continue to Stripe <ArrowUpRight className="size-4" />
              </Button>
            </div>
          </div>
        </Modal>

        <Modal.Confirm
          open={confirmDisconnect}
          onOpenChange={setConfirmDisconnect}
          title="Disconnect Stripe?"
          body="This unlinks your Stripe account from Bokapsys. Your Stripe account, funds, and history stay intact in Stripe - you can reconnect any time. New invoices won't accept payment until you pick a mode again."
          confirmLabel="Disconnect"
          confirmVariant="danger"
          onConfirm={onDisconnect}
          busy={disconnect.isPending}
        />
      </CardContent>
    </Card>
  )
}

function ConnectStatusBlock({
  status,
  ready,
  canEdit,
  syncing,
  onSync,
  onResume,
  onDashboard,
  onDisconnect,
  resuming,
}: {
  status: {
    stripeAccountId: string | null
    country: string | null
    chargesEnabled: boolean
    payoutsEnabled: boolean
    detailsSubmitted: boolean
    syncedAt: string | null
  } | undefined
  ready: boolean
  canEdit: boolean
  syncing: boolean
  onSync: () => void
  onResume: () => void
  onDashboard: () => void
  onDisconnect: () => void
  resuming: boolean
}) {
  if (!status) return null
  const stage = !status.detailsSubmitted
    ? 'not_started'
    : !status.chargesEnabled
      ? 'pending'
      : 'active'

  return (
    <div className="rounded-md border border-border p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {ready ? (
            <CheckCircle2 className="size-5 text-success" />
          ) : (
            <AlertTriangle className="size-5 text-warning" />
          )}
          <div>
            <div className="font-semibold">
              {stage === 'active' && 'Ready to accept payments'}
              {stage === 'pending' && 'Stripe is verifying your details'}
              {stage === 'not_started' && 'Onboarding not finished'}
            </div>
            <div className="text-xs text-muted-foreground">
              {status.stripeAccountId ?? '-'}
              {status.country ? ` · ${status.country}` : ''}
              {status.syncedAt ? ` · synced ${new Date(status.syncedAt).toLocaleString()}` : ''}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {stage !== 'active' && canEdit ? (
            <Button onClick={onResume} disabled={resuming} variant="primary" size="sm">
              {resuming ? <Loader2 className="size-4 animate-spin" /> : <ArrowUpRight className="size-4" />}
              {stage === 'not_started' ? 'Continue onboarding' : 'Update details'}
            </Button>
          ) : null}
          {stage === 'active' ? (
            <Button onClick={onDashboard} variant="secondary" size="sm">
              <ExternalLink className="size-4" /> Stripe dashboard
            </Button>
          ) : null}
          <Button onClick={onSync} disabled={syncing} variant="ghost" size="sm">
            {syncing ? <Loader2 className="size-4 animate-spin" /> : <RefreshCcw className="size-4" />}
            Sync
          </Button>
          {canEdit ? (
            <Button onClick={onDisconnect} variant="ghost" size="sm">
              <Unplug className="size-4" /> Disconnect
            </Button>
          ) : null}
        </div>
      </div>

      <div className="mt-3 grid gap-2 text-xs sm:grid-cols-3">
        <StatusPill ok={status.detailsSubmitted} label="Details submitted" />
        <StatusPill ok={status.chargesEnabled} label="Charges enabled" />
        <StatusPill ok={status.payoutsEnabled} label="Payouts enabled" />
      </div>
    </div>
  )
}

function StatusPill({ ok, label }: { ok: boolean; label: string }) {
  return (
    <div className={cn(
      'flex items-center gap-1.5 rounded-md border px-2 py-1',
      ok ? 'border-success/40 bg-success/5 text-success' : 'border-muted-foreground/20 text-muted-foreground',
    )}>
      {ok ? <CheckCircle2 className="size-3.5" /> : <Clock className="size-3.5" />}
      <span>{label}</span>
    </div>
  )
}

// ──────────────────────────────────────────────────────────────────────
// Payout destination block (Bokapsys-managed mode only).
// ──────────────────────────────────────────────────────────────────────
//
// Tenant tells Bokapsys where to send their payouts. Reuses the
// existing tenant_bank_accounts table; the row carrying
// is_payout_destination = TRUE is the one we pay to. Editing creates a
// new row + flips the flag so historical payouts retain their original
// account snapshot.

function PayoutDestinationBlock({ canEdit }: { canEdit: boolean }) {
  const { toast } = useToast()
  const accountQ = usePayoutAccount()
  const setAccount = useSetPayoutAccount()
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState({
    label: '',
    bankName: '',
    accountName: '',
    accountNumber: '',
    routingNumber: '',
    sortCode: '',
    iban: '',
    swift: '',
  })

  const account = accountQ.data?.account ?? null

  const onOpen = () => {
    setForm({
      label: account?.label ?? 'Primary payout account',
      bankName: account?.bankName ?? '',
      accountName: account?.accountName ?? '',
      accountNumber: '',
      routingNumber: account?.routingNumber ?? '',
      sortCode: account?.sortCode ?? '',
      iban: '',
      swift: account?.swift ?? '',
    })
    setOpen(true)
  }

  const onSubmit = async () => {
    try {
      await setAccount.mutateAsync({
        label: form.label.trim(),
        bankName: form.bankName.trim() || null,
        accountName: form.accountName.trim() || null,
        accountNumber: form.accountNumber.trim() || null,
        routingNumber: form.routingNumber.trim() || null,
        sortCode: form.sortCode.trim() || null,
        iban: form.iban.trim() || null,
        swift: form.swift.trim() || null,
      })
      toast({ title: 'Payout account saved.' })
      setOpen(false)
    } catch (err) {
      toast({ variant: 'destructive', title: 'Could not save', description: messageFor(err, '') })
    }
  }

  return (
    <div className="rounded-md border p-4">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Landmark className="size-5 text-primary" />
          <h3 className="text-base font-semibold">Where should we pay you?</h3>
        </div>
        {account ? <Badge variant="success">Configured</Badge> : <Badge variant="warning">Not set</Badge>}
      </div>
      <p className="text-sm text-muted-foreground">
        Bokapsys collects payments on its Stripe account and pays you on a recurring schedule.
        Tell us which bank account to pay to. We&apos;ll deduct Stripe&apos;s processing fees and a
        1% platform fee from each payout.
      </p>
      {account ? (
        <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
          <ReadonlyRow label="Label" value={account.label} />
          <ReadonlyRow label="Bank" value={account.bankName ?? '-'} />
          <ReadonlyRow label="Account holder" value={account.accountName ?? '-'} />
          <ReadonlyRow
            label="Account"
            value={account.accountNumberLast4
              ? `••••${account.accountNumberLast4}`
              : (account.iban ?? '-')}
          />
          <ReadonlyRow
            label="Routing / sort"
            value={account.routingNumber ?? account.sortCode ?? '-'}
          />
          <ReadonlyRow label="SWIFT" value={account.swift ?? '-'} />
        </div>
      ) : null}
      <div className="mt-3 flex flex-wrap gap-2">
        <Button onClick={onOpen} disabled={!canEdit} variant={account ? 'secondary' : 'primary'}>
          {account ? 'Update payout account' : 'Add payout account'}
        </Button>
      </div>

      <Modal open={open} onOpenChange={setOpen} title="Payout bank account" className="max-w-3xl">
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Updating this creates a new entry - historical payouts keep their original
            account on file for audit.
          </p>
          <FormField
            id="payout-label"
            label="Label"
            hint="Internal name for this account (e.g. 'Primary checking')"
            control={
              <Input
                id="payout-label"
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.currentTarget.value })}
              />
            }
          />
          <div className="grid gap-3 sm:grid-cols-2">
            <FormField
              id="payout-bankname"
              label="Bank name"
              control={
                <Input
                  id="payout-bankname"
                  value={form.bankName}
                  onChange={(e) => setForm({ ...form, bankName: e.currentTarget.value })}
                />
              }
            />
            <FormField
              id="payout-accountname"
              label="Account holder"
              control={
                <Input
                  id="payout-accountname"
                  value={form.accountName}
                  onChange={(e) => setForm({ ...form, accountName: e.currentTarget.value })}
                />
              }
            />
            <FormField
              id="payout-acctnum"
              label="Account number"
              hint="US: full account number. UK: account number. Leave blank if using IBAN."
              control={
                <Input
                  id="payout-acctnum"
                  value={form.accountNumber}
                  onChange={(e) => setForm({ ...form, accountNumber: e.currentTarget.value })}
                  inputMode="numeric"
                />
              }
            />
            <FormField
              id="payout-routing"
              label="Routing / sort"
              hint="Routing (9 digits) or sort code (6 digits)."
              control={
                <Input
                  id="payout-routing"
                  value={form.routingNumber || form.sortCode}
                  onChange={(e) => {
                    const v = e.currentTarget.value
                    // Auto-route into the right column based on length pattern.
                    if (/^\d{0,9}$/.test(v) && v.length <= 9) {
                      setForm({ ...form, routingNumber: v, sortCode: '' })
                    }
                  }}
                  inputMode="numeric"
                />
              }
            />
            <FormField
              id="payout-iban"
              label="IBAN"
              hint="EU/international wire. Leave blank for US/UK."
              control={
                <Input
                  id="payout-iban"
                  value={form.iban}
                  onChange={(e) => setForm({ ...form, iban: e.currentTarget.value.toUpperCase() })}
                />
              }
            />
            <FormField
              id="payout-swift"
              label="SWIFT / BIC"
              hint="Required for international wires."
              control={
                <Input
                  id="payout-swift"
                  value={form.swift}
                  onChange={(e) => setForm({ ...form, swift: e.currentTarget.value.toUpperCase() })}
                />
              }
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={onSubmit} disabled={setAccount.isPending}>
              {setAccount.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
              Save payout account
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

function ReadonlyRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-sm">{value}</div>
    </div>
  )
}
